import React, { useState, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';

const BatchRunForm = ({ datasetId, onComplete }) => {
  const { getAuthHeader } = useContext(AuthContext);
  
  // Source selection
  const [sourceType, setSourceType] = useState('json'); // 'json', 'csv', 'googleSheets'
  
  // File/data input
  const [jsonData, setJsonData] = useState('');
  const [csvData, setCsvData] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetName, setSheetName] = useState('Sheet1');
  
  // Options
  const [skipRows, setSkipRows] = useState('');
  const [limit, setLimit] = useState('');
  const [batchSize, setBatchSize] = useState('50'); // NEW: Batch size for chunked processing
  const [sendToDestinations, setSendToDestinations] = useState(true);
  
  // State
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  
  // NEW: Batch progress tracking
  const [batchProgress, setBatchProgress] = useState(null);
  const [abortController, setAbortController] = useState(null);
  
  // File input ref
  const fileInputRef = useRef(null);

  // Parse skipRows input to array of numbers
  const parseSkipRows = (input) => {
    if (!input.trim()) return [];
    return input
      .split(',')
      .map(s => s.trim())
      .filter(s => s)
      .map(s => {
        // Handle ranges like "1-5"
        if (s.includes('-')) {
          const [start, end] = s.split('-').map(n => parseInt(n.trim()));
          const range = [];
          for (let i = start; i <= end; i++) {
            range.push(i);
          }
          return range;
        }
        return parseInt(s);
      })
      .flat()
      .filter(n => !isNaN(n));
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      
      if (file.name.endsWith('.json')) {
        setSourceType('json');
        setJsonData(content);
      } else if (file.name.endsWith('.csv')) {
        setSourceType('csv');
        setCsvData(content);
      } else {
        setError('Please upload a .json or .csv file');
      }
    };
    reader.readAsText(file);
  };

  // Preview data
  const handlePreview = async () => {
    setError(null);
    setPreviewing(true);
    setPreview(null);

    try {
      const config = {
        headers: getAuthHeader(),
      };

      let requestData = { sourceType };

      switch (sourceType) {
        case 'json':
          try {
            requestData.data = JSON.parse(jsonData);
          } catch (e) {
            throw new Error('Invalid JSON format');
          }
          break;
        case 'csv':
          requestData.data = csvData;
          break;
        case 'googleSheets':
          requestData.spreadsheetId = spreadsheetId;
          requestData.sheetName = sheetName;
          break;
        default:
          break;
      }

      const { data } = await axios.post(
        `/api/datasets/${datasetId}/batch/preview`,
        requestData,
        config
      );

      setPreview(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to preview data');
    } finally {
      setPreviewing(false);
    }
  };

  // NEW: Run batch enrichment with chunked processing
  const handleRunBatch = async () => {
    setError(null);
    setLoading(true);
    setResults(null);
    setBatchProgress(null);

    const controller = new AbortController();
    setAbortController(controller);

    try {
      const config = {
        headers: getAuthHeader(),
        signal: controller.signal
      };

      // First, get the total count via preview
      let previewData = { sourceType };
      
      switch (sourceType) {
        case 'json':
          try {
            previewData.data = JSON.parse(jsonData);
          } catch (e) {
            throw new Error('Invalid JSON format');
          }
          break;
        case 'csv':
          previewData.data = csvData;
          break;
        case 'googleSheets':
          previewData.spreadsheetId = spreadsheetId;
          previewData.sheetName = sheetName;
          break;
        default:
          break;
      }

      // Get total record count
      const previewResponse = await axios.post(
        `/api/datasets/${datasetId}/batch/preview`,
        previewData,
        config
      );
      
      const totalRecords = previewResponse.data.totalRecords;
      const parsedBatchSize = parseInt(batchSize) || 50;
      const parsedLimit = limit ? parseInt(limit) : totalRecords;
      const initialSkipRows = parseSkipRows(skipRows);
      const initialSkipCount = initialSkipRows.length > 0 ? Math.max(...initialSkipRows) : 0;
      
      // Calculate effective total after skips and limits
      const effectiveTotal = Math.min(totalRecords - initialSkipCount, parsedLimit);
      const totalBatches = Math.ceil(effectiveTotal / parsedBatchSize);

      // Initialize progress tracking
      const progressState = {
        totalRecords: effectiveTotal,
        totalBatches,
        currentBatch: 0,
        processedRecords: 0,
        enrichedRecords: 0,
        errors: 0,
        batches: [],
        allResults: [],
        allErrors: [],
        destinationResults: []
      };
      
      setBatchProgress({ ...progressState });

      // Process batches sequentially
      for (let batchNum = 0; batchNum < totalBatches; batchNum++) {
        if (controller.signal.aborted) {
          throw new Error('Batch processing cancelled');
        }

        const batchStart = batchNum * parsedBatchSize;
        const currentBatchLimit = Math.min(parsedBatchSize, effectiveTotal - batchStart);
        
        // Calculate skip rows for this batch
        // We need to skip: initial skip rows + all rows from previous batches
        const batchSkipRows = [
          ...initialSkipRows,
          // Add rows 1 to batchStart (1-indexed) to skip previous batches
          ...Array.from({ length: batchStart }, (_, i) => initialSkipCount + i + 1)
        ];

        // Update progress
        progressState.currentBatch = batchNum + 1;
        setBatchProgress({ ...progressState });

        // Build request for this batch
        let requestData = { 
          sourceType,
          sendToDestinations,
          limit: currentBatchLimit
        };

        switch (sourceType) {
          case 'json':
            requestData.data = JSON.parse(jsonData);
            break;
          case 'csv':
            requestData.data = csvData;
            break;
          case 'googleSheets':
            requestData.spreadsheetId = spreadsheetId;
            requestData.sheetName = sheetName;
            break;
          default:
            break;
        }

        if (batchSkipRows.length > 0) {
          requestData.skipRows = batchSkipRows;
        }

        try {
          const { data } = await axios.post(
            `/api/datasets/${datasetId}/batch`,
            requestData,
            config
          );

          // Update progress with batch results
          const batchResult = {
            batchNumber: batchNum + 1,
            success: true,
            enriched: data.summary.enriched || 0,
            errors: data.summary.errors || 0,
            sentToDestinations: data.summary.sentToDestinations || 0,
            destinationResults: data.destinationResults
          };

          progressState.batches.push(batchResult);
          progressState.processedRecords += (data.summary.enriched || 0) + (data.summary.errors || 0);
          progressState.enrichedRecords += data.summary.enriched || 0;
          progressState.errors += data.summary.errors || 0;
          
          // Collect all results and errors
          if (data.results) {
            // Adjust indices to be global
            const adjustedResults = data.results.map(r => ({
              ...r,
              index: batchStart + r.index,
              batch: batchNum + 1
            }));
            progressState.allResults.push(...adjustedResults);
          }
          if (data.errors) {
            const adjustedErrors = data.errors.map(e => ({
              ...e,
              index: batchStart + e.index,
              batch: batchNum + 1
            }));
            progressState.allErrors.push(...adjustedErrors);
          }
          if (data.destinationResults) {
            progressState.destinationResults.push({
              batch: batchNum + 1,
              results: data.destinationResults
            });
          }

          setBatchProgress({ ...progressState });

        } catch (batchErr) {
          if (batchErr.name === 'CanceledError' || controller.signal.aborted) {
            throw new Error('Batch processing cancelled');
          }
          
          // Record batch failure but continue
          progressState.batches.push({
            batchNumber: batchNum + 1,
            success: false,
            error: batchErr.response?.data?.message || batchErr.message,
            enriched: 0,
            errors: currentBatchLimit
          });
          progressState.errors += currentBatchLimit;
          progressState.processedRecords += currentBatchLimit;
          
          setBatchProgress({ ...progressState });
        }
      }

      // Compile final results
      const finalResults = {
        success: true,
        summary: {
          totalRecords: progressState.totalRecords,
          enriched: progressState.enrichedRecords,
          errors: progressState.errors,
          sentToDestinations: progressState.enrichedRecords,
          totalBatches: progressState.totalBatches,
          batchSize: parsedBatchSize
        },
        batchProgress: progressState,
        results: progressState.allResults,
        errors: progressState.allErrors,
        destinationResults: progressState.destinationResults.length > 0 
          ? { results: progressState.destinationResults }
          : null
      };

      setResults(finalResults);
      if (onComplete) onComplete(finalResults);

    } catch (err) {
      if (err.message === 'Batch processing cancelled') {
        setError('Batch processing was cancelled');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to run batch enrichment');
      }
    } finally {
      setLoading(false);
      setAbortController(null);
    }
  };

  // NEW: Cancel batch processing
  const handleCancelBatch = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  // Reset form
  const handleReset = () => {
    setSourceType('json');
    setJsonData('');
    setCsvData('');
    setSpreadsheetId('');
    setSheetName('Sheet1');
    setSkipRows('');
    setLimit('');
    setBatchSize('50');
    setSendToDestinations(true);
    setError(null);
    setPreview(null);
    setResults(null);
    setBatchProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Source Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Data Source
        </label>
        <div className="flex space-x-4">
          {[
            { id: 'json', label: 'JSON File', icon: '{ }' },
            { id: 'csv', label: 'CSV File', icon: '📊' },
            { id: 'googleSheets', label: 'Google Sheets', icon: '📋' },
          ].map((source) => (
            <button
              key={source.id}
              type="button"
              onClick={() => {
                setSourceType(source.id);
                setPreview(null);
                setResults(null);
              }}
              className={`flex-1 flex items-center justify-center px-4 py-3 border rounded-lg transition-colors ${
                sourceType === source.id
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="mr-2">{source.icon}</span>
              {source.label}
            </button>
          ))}
        </div>
      </div>

      {/* File Upload (for JSON and CSV) */}
      {(sourceType === 'json' || sourceType === 'csv') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={sourceType === 'json' ? '.json' : '.csv'}
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
          />
        </div>
      )}

      {/* JSON Input */}
      {sourceType === 'json' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Or Paste JSON Array
          </label>
          <textarea
            value={jsonData}
            onChange={(e) => setJsonData(e.target.value)}
            placeholder='[{"name": "John", "email": "john@example.com"}, {"name": "Jane", "email": "jane@example.com"}]'
            rows={8}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono text-sm"
          />
        </div>
      )}

      {/* CSV Input */}
      {sourceType === 'csv' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Or Paste CSV Content
          </label>
          <textarea
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            placeholder="name,email&#10;John,john@example.com&#10;Jane,jane@example.com"
            rows={8}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono text-sm"
          />
        </div>
      )}

      {/* Google Sheets Input */}
      {sourceType === 'googleSheets' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Spreadsheet ID
            </label>
            <input
              type="text"
              value={spreadsheetId}
              onChange={(e) => setSpreadsheetId(e.target.value)}
              placeholder="e.g., 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Find this in the Google Sheets URL: docs.google.com/spreadsheets/d/<strong>[SPREADSHEET_ID]</strong>/edit
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sheet Name (Tab)
            </label>
            <input
              type="text"
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder="Sheet1"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> The Google Sheet must be shared with the service account email for access.
            </p>
          </div>
        </div>
      )}

      {/* Options */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skip Rows (optional)
            </label>
            <input
              type="text"
              value={skipRows}
              onChange={(e) => setSkipRows(e.target.value)}
              placeholder="e.g., 1, 3, 5-10"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Row numbers to skip (1-indexed). Supports ranges like "5-10"
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Limit (optional)
            </label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="e.g., 100"
              min="1"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum total rows to process
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Batch Size
            </label>
            <input
              type="number"
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
              placeholder="50"
              min="1"
              max="500"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Records per batch (default: 50)
            </p>
          </div>
        </div>
        
        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={sendToDestinations}
              onChange={(e) => setSendToDestinations(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Send enriched data to configured destinations
            </span>
          </label>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing || loading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {previewing ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Previewing...
            </>
          ) : (
            'Preview Data'
          )}
        </button>

        {!loading ? (
          <button
            type="button"
            onClick={handleRunBatch}
            disabled={loading || previewing}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            Run Batch Enrichment
          </button>
        ) : (
          <button
            type="button"
            onClick={handleCancelBatch}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Cancel (Running...)
          </button>
        )}

        <button
          type="button"
          onClick={handleReset}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
        >
          Reset
        </button>
      </div>

      {/* Batch Progress Display */}
      {batchProgress && loading && (
        <div className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50">
          <div className="px-4 py-3 border-b border-blue-200">
            <h3 className="text-sm font-medium text-blue-900">
              🔄 Batch Processing in Progress
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Overall Progress Bar */}
            <div>
              <div className="flex justify-between text-sm text-blue-700 mb-1">
                <span>Overall Progress</span>
                <span>
                  Batch {batchProgress.currentBatch} of {batchProgress.totalBatches}
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(batchProgress.currentBatch / batchProgress.totalBatches) * 100}%` 
                  }}
                ></div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="bg-white rounded-md p-2">
                <div className="text-lg font-semibold text-gray-900">{batchProgress.totalRecords}</div>
                <div className="text-xs text-gray-500">Total Records</div>
              </div>
              <div className="bg-white rounded-md p-2">
                <div className="text-lg font-semibold text-green-600">{batchProgress.enrichedRecords}</div>
                <div className="text-xs text-gray-500">Enriched</div>
              </div>
              <div className="bg-white rounded-md p-2">
                <div className="text-lg font-semibold text-red-600">{batchProgress.errors}</div>
                <div className="text-xs text-gray-500">Errors</div>
              </div>
              <div className="bg-white rounded-md p-2">
                <div className="text-lg font-semibold text-blue-600">{batchProgress.batches.length}</div>
                <div className="text-xs text-gray-500">Batches Done</div>
              </div>
            </div>

            {/* Batch Status List */}
            {batchProgress.batches.length > 0 && (
              <div className="max-h-40 overflow-y-auto">
                <div className="text-xs font-medium text-blue-800 mb-2">Completed Batches:</div>
                <div className="space-y-1">
                  {batchProgress.batches.map((batch, idx) => (
                    <div 
                      key={idx}
                      className={`text-xs px-2 py-1 rounded flex justify-between ${
                        batch.success 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      <span>Batch {batch.batchNumber}</span>
                      <span>
                        {batch.success 
                          ? `✓ ${batch.enriched} enriched${batch.errors > 0 ? `, ${batch.errors} errors` : ''}`
                          : `✗ ${batch.error}`
                        }
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Results */}
      {preview && !loading && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">
              Data Preview ({preview.totalRecords} total records)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  {preview.columns.map((col) => (
                    <th key={col} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {preview.preview.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    {preview.columns.map((col) => (
                      <td key={col} className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {typeof row[col] === 'object' 
                          ? JSON.stringify(row[col]) 
                          : String(row[col] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.totalRecords > preview.preview.length && (
            <div className="bg-gray-50 px-4 py-2 text-sm text-gray-500 text-center">
              Showing {preview.preview.length} of {preview.totalRecords} records
            </div>
          )}
        </div>
      )}

      {/* Batch Results */}
      {results && !loading && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className={`px-4 py-3 border-b border-gray-200 ${
            results.summary.errors > 0 ? 'bg-yellow-50' : 'bg-green-50'
          }`}>
            <h3 className="text-sm font-medium text-gray-900">
              ✅ Batch Enrichment Complete
            </h3>
            <div className="mt-1 flex flex-wrap gap-4 text-sm">
              <span className="text-green-600">
                ✓ {results.summary.enriched || results.summary.processed} enriched
              </span>
              {results.summary.errors > 0 && (
                <span className="text-red-600">
                  ✗ {results.summary.errors} errors
                </span>
              )}
              {results.summary.totalBatches && (
                <span className="text-purple-600">
                  📦 {results.summary.totalBatches} batches (size: {results.summary.batchSize})
                </span>
              )}
              {results.summary.sentToDestinations > 0 && (
                <span className="text-blue-600">
                  📤 {results.summary.sentToDestinations} sent to destinations
                </span>
              )}
            </div>
          </div>

          {/* Batch Summary */}
          {results.batchProgress && results.batchProgress.batches.length > 0 && (
            <div className="p-4 border-b border-gray-200 bg-purple-50">
              <h4 className="text-sm font-medium text-purple-900 mb-2">
                📦 Batch Processing Summary
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {results.batchProgress.batches.map((batch, idx) => (
                  <div 
                    key={idx}
                    className={`text-xs px-2 py-1 rounded text-center ${
                      batch.success 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    <div className="font-medium">Batch {batch.batchNumber}</div>
                    <div>
                      {batch.success 
                        ? `${batch.enriched} enriched`
                        : 'Failed'
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Destination Results */}
          {results.destinationResults && (
            <div className="p-4 border-b border-gray-200 bg-blue-50">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                📤 Destination Results (Bulk Send per Batch)
              </h4>
              <div className="space-y-2">
                {/* Handle array of batch destination results */}
                {Array.isArray(results.destinationResults.results) && results.destinationResults.results.map((batchDest, batchIdx) => (
                  <div key={batchIdx} className="border border-blue-200 rounded p-2 bg-white">
                    <div className="text-xs font-medium text-blue-700 mb-1">Batch {batchDest.batch}</div>
                    {batchDest.results?.results?.map((destResult, idx) => (
                      <div 
                        key={idx} 
                        className={`text-xs p-1 rounded mb-1 ${
                          destResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        <span className="font-medium capitalize">{destResult.destinationType}:</span>
                        {destResult.success ? (
                          <span> ✓ {destResult.recordCount || destResult.recordsSent} records
                            {destResult.rowsAppended !== undefined && ` (${destResult.rowsAppended} appended)`}
                          </span>
                        ) : (
                          <span> ✗ {destResult.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                {/* Handle legacy single destination result format */}
                {!Array.isArray(results.destinationResults.results) && results.destinationResults.results?.map((destResult, idx) => (
                  <div 
                    key={idx} 
                    className={`text-sm p-2 rounded ${
                      destResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    <span className="font-medium capitalize">{destResult.destinationType}:</span>
                    {destResult.success ? (
                      <span> ✓ {destResult.recordCount || destResult.recordsSent} records sent
                        {destResult.rowsAppended !== undefined && ` (${destResult.rowsAppended} appended, ${destResult.rowsUpdated} updated)`}
                      </span>
                    ) : (
                      <span> ✗ Failed: {destResult.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Successful Results */}
          {results.results.length > 0 && (
            <div className="p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Enriched Results (showing first 10)
              </h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {results.results.slice(0, 10).map((result) => (
                  <details key={result.index} className="border border-gray-200 rounded-md">
                    <summary className="px-3 py-2 bg-gray-50 cursor-pointer text-sm font-medium text-gray-700">
                      Row {result.index}
                    </summary>
                    <div className="p-3 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-gray-600 mb-1">Original</h5>
                          <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                            {JSON.stringify(result.original, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-600 mb-1">Enriched</h5>
                          <pre className="bg-green-50 p-2 rounded text-xs overflow-x-auto">
                            {JSON.stringify(result.enriched, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Errors */}
          {results.errors.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-red-700 mb-2">
                Errors ({results.errors.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {results.errors.map((err) => (
                  <div key={err.index} className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                    <div className="font-medium text-red-700">Row {err.index}</div>
                    <div className="text-red-600">{err.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Download Results Button */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `batch-results-${new Date().toISOString().slice(0, 10)}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Results JSON
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchRunForm;
